import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "../../../lib/utils/cn";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        outlineSecondary:
          "hover:border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-2xl",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        active:
          "bg-[#C0DE60] bg-opacity-10 text-[#C0DE60] border border-[#C0DE60] hover:bg-[#5FD5DE] hover:bg-opacity-10",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        brand: "bg-gradient-to-r from-[#5FD5DE] to-[#C0DE60] text-white shadow-md",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
        active: "rounded-[47px] px-3",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

// Übersetzer (Chrome, DeepL) hängen nackte Textknoten um und wickeln sie in <font>-Elemente.
// Steht daneben ein bedingt gerendertes Element (z. B. ein Lade-Spinner), ist Reacts Anker-Knoten
// danach kein Kind des Buttons mehr und insertBefore wirft einen NotFoundError.
// Elemente werden dagegen nie umgehängt - deshalb bekommt roher Text hier ein eigenes <span>.
const wrapTextChildren = (children: React.ReactNode): React.ReactNode =>
  React.Children.map(children, (child) =>
    typeof child === "string" || typeof child === "number" ? <span>{child}</span> : child
  );

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props}>
        {/* asChild reicht an Radix' Slot durch, das genau ein Element-Kind erwartet - hier nicht anfassen. */}
        {asChild ? children : wrapTextChildren(children)}
      </Comp>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
